import { IoInformationCircleOutline } from "react-icons/io5";
import { HiDotsHorizontal } from "react-icons/hi";
import Modal from "@/components/modal";
import SelectUser from "@/components/selectUser";
import SingleSelect from "@/components/SingleSelect";
import MultiSelect from "@/components/MultiSelect";
import RichTextEditorSun from "@/components/RichTextEditor";
import { DebounceInput } from "react-debounce-input";
import { RiSendPlaneFill } from "react-icons/ri";
import moment from "moment";
import { useState, useEffect } from "react";
import api from "@/services/api";

export default function TodoModal({ todo, isOpen, onClose, onDelete, updateField, onAddComment }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (todo) {
      getFiles();
    }
  }, [todo]);

  if (!todo) return null;

  async function getFiles() {
    try {
      const { ok, data } = await api.post(`/mission_ao_file/search`, { mission_ao_answer_id: todo.mission_ao_answer_id });
      if (!ok) throw new Error("Failed to fetch files");
      setFiles(data);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching files");
    }
  }

  const handleDeleteComment = (commentId) => {
    const newComments = todo.comments.filter((comment) => comment._id !== commentId);
    updateField(todo._id, "comments", newComments);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full h-[90vh]  overflow-y-scroll">
        <div className="bg-white flex flex-col w-full overflow-hidden">
          <div className="bg-white flex flex-col flex-1 rounded-lg h-full">
            <div className="flex justify-between items-center gap-2 pt-4 px-4 mr-4">
              <DebounceInput
                minLength={1}
                debounceTimeout={1500}
                className="input text-base font-semibold flex-1"
                placeholder="Title..."
                type="text"
                id="title"
                value={todo?.title || ""}
                onChange={(e) => updateField(todo._id, "title", e.target.value)}
              />
              <div className="relative">
                <button className="transparent-btn" onClick={() => setShowDropdown(!showDropdown)}>
                  <HiDotsHorizontal />
                </button>
                <div className={`text-sm fixed right-2 top-[3rem] bg-white border border-gray-200 rounded-md shadow-md min-w-40 z-10 ${!showDropdown && "hidden"}`}>
                  <button
                    className="flex items-center gap-2 px-4 py-2 w-full hover:bg-gray-100 text-red-600"
                    onClick={() => {
                      onDelete(todo._id);
                      setShowDropdown(false);
                    }}>
                    <span>Delete</span>
                  </button>
                </div>
                {showDropdown && <div className="fixed inset-0 z-0" onClick={() => setShowDropdown(false)} />}
              </div>
            </div>

            <div className="p-6 flex gap-6 flex-1">
              {/* Left Column */}
              <div className="w-1/3 flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Lead</label>
                  <div className="flex items-center gap-2 mt-1">
                    {todo?.lead_user_avatar && <img src={todo.lead_user_avatar} alt="" className="w-8 h-8 rounded-full" />}
                    <SelectUser
                      value={todo?.lead_user_id}
                      onChange={(user) => {
                        updateField(todo._id, "lead_user_id", user.id);
                        updateField(todo._id, "lead_user_name", user.name);
                        updateField(todo._id, "lead_user_avatar", user.avatar);
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <input
                    type="date"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    value={todo?.date?.split("T")[0]}
                    onChange={(e) => updateField(todo._id, "date", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <SingleSelect
                    options={[
                      { value: "pending", label: "Pending" },
                      { value: "in_progress", label: "In Progress" },
                      { value: "read_again", label: "Read Again" },
                      { value: "completed", label: "Completed" },
                      { value: "need_info", label: "Need Info" },
                    ]}
                    value={{
                      value: todo?.status,
                      label: todo?.status?.charAt(0).toUpperCase() + todo?.status?.slice(1).replace(/_/g, " "),
                    }}
                    onChange={(e) => {
                      updateField(todo._id, "status", e.value);
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Documents</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <MultiSelect
                      id="todo-documents"
                      options={files.map((file) => ({
                        value: file._id,
                        label: file.label,
                      }))}
                      values={
                        todo?.documents?.map((doc) => ({
                          value: doc.id,
                          label: doc.label,
                        })) || []
                      }
                      onSelectedChange={(selected) => {
                        updateField(
                          todo._id,
                          "documents",
                          selected.map((s) => ({
                            id: s.value,
                            label: s.label,
                          })),
                        );
                      }}
                      placeholder="Select documents..."
                    />
                  </div>
                  <div className="mt-2">
                    {todo?.documents?.map((doc) => (
                      <a
                        key={doc.id}
                        href={`/rfp/answers/${todo.mission_ao_answer_id}/file/${doc.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-3 py-1 m-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm">
                        {doc.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Comments */}
              <div className="w-2/3 flex flex-col">
                <div>
                  <RichTextEditorSun
                    buttonList={[["bold", "italic", "link", "image"]]}
                    onChange={(e) => {
                      setText(e);
                    }}
                    placeholder="Add a comment..."
                    height="100px"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => {
                        onAddComment(todo._id, text);
                        setText("");
                      }}
                      className="btn btn-primary rounded-full px-6 py-3 flex items-center gap-2">
                      <span>Send</span>
                      <RiSendPlaneFill className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="flex-grow overflow-y-auto mt-4">
                  <div className="flex flex-col gap-y-4">
                    {(todo?.comments || [])
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((comment, index) => (
                        <div key={index} className="flex flex-col bg-gray-50 p-4 rounded-lg shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img src={comment?.user_avatar} className="rounded-full w-8 h-8 border-2 border-white shadow-sm" alt={comment?.user_name} />
                              <div>
                                <div className="font-medium text-gray-900">{comment?.user_name}</div>
                                <div className="text-xs text-gray-500">{moment(new Date(comment.date)).fromNow()}</div>
                              </div>
                            </div>
                            <button onClick={() => handleDeleteComment(comment._id)} className="text-red-500 hover:text-red-700 text-sm">
                              Delete
                            </button>
                          </div>
                          <div className="mt-3 text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: comment.text }} />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
